import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  createFilterOptions,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  InfoOutlined as InfoOutlinedIcon,
  People as PeopleIcon,
  Class as ClassIcon,
  Schedule as ScheduleIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  DriveFileRenameOutline as RenameIcon,
  CallMerge as CallMergeIcon,
  WarningAmber as WarningAmberIcon,
  Domain as DomainIcon,
} from '@mui/icons-material';
import config from '../../config';
import { adminColors, radius, TOUCH_HIT_SX } from '../../theme/adminTheme';
import { authFetch, handleApiResponse } from '../../api/authFetch';
import { describeAdminError } from '../../utils/adminErrors';
import { isMasterAdmin as isMasterAdminRole } from '../../constants/roles';
import { DataTable, FilterBar, EmptyState } from './primitives';
import { useQueryState } from '../../hooks/useQueryState';
import ScheduleTemplateDialog from './ScheduleTemplateDialog';

// ─── Helpers ─────────────────────────────────────────────────────────────────
// Carried over from the retired standalone SchoolTimezoneSettings.js unchanged —
// per-school-zone local time rendering, US-first zone grouping, and the AA-safe
// abbreviation color all went through review; re-deriving them would be a
// regression risk, not a fresh start.

// "America/New_York" -> "America / New York"
function formatTzName(tz) {
  if (!tz) return '';
  return tz.split('/').map((seg) => seg.replace(/_/g, ' ')).join(' / ');
}

// Short zone abbreviation, e.g. "EDT". Falls back gracefully if unsupported.
function getTzAbbreviation(tz, date = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(date);
    return parts.find((p) => p.type === 'timeZoneName')?.value || '';
  } catch {
    return '';
  }
}

function formatClock(tz, date = new Date()) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch {
    return null;
  }
}

// Live-updating clock string for a given IANA zone. Used in the change dialog
// to confirm "what time is it there right now" — minute precision is all a
// timezone-comparison decision needs, so this ticks once a minute rather than
// once a second. The table itself intentionally stays static (no ticking wall
// of numbers) and is refreshed on demand instead.
function useLiveClock(tz) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!tz) return undefined;
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, [tz]);
  return tz ? formatClock(tz, now) : null;
}

function formatCount(n) {
  return typeof n === 'number' ? n.toLocaleString() : '—';
}

const tzFilter = createFilterOptions({
  stringify: (option) => `${option.label} ${option.id}`,
});

// Grouped, US-first timezone picker — the one piece of TimezoneChangeDialog
// this file leaned on before Phase 3, now shared with the Add/Rename school
// dialogs below rather than tripled. Behaviour is unchanged from the
// original inline Autocomplete: same grouping, same filter-by-label-or-id,
// same AA-safe abbreviation column, same "search" affordance in the input.
function TimezoneAutocomplete({ value, onChange, tzOptions, tzLoadError, disabled, label = 'Timezone', autoFocus = false }) {
  if (tzLoadError) {
    return <Alert severity="error" sx={{ mb: 2 }}>{tzLoadError}</Alert>;
  }
  return (
    <Autocomplete
      options={tzOptions}
      groupBy={(option) => option.group}
      value={value}
      onChange={(_, val) => onChange(val)}
      getOptionLabel={(option) => option.label || ''}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={tzFilter}
      disabled={disabled}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={key} {...rest} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <span>{option.label}</span>
            {/* text.secondary (#6b7280, 4.83:1 on white) — the previous
                #9e9e9e measured 2.68:1 and failed AA on every row */}
            <span style={{ color: '#6b7280', fontSize: 12 }}>{option.abbr}</span>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder="Search by city, region, or zone ID"
          autoFocus={autoFocus}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {/* #71717a to match the filter search icon in SchoolsTable below
                    and UsersView — was #9e9e9e, two grays for one icon role. */}
                <SearchIcon sx={{ color: '#71717a', ml: 0.5, mr: -0.5 }} fontSize="small" />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

// ─── Timezone change dialog ──────────────────────────────────────────────────

function TimezoneChangeDialog({ open, school, tzOptions, tzLoadError, onClose, onSaved }) {
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // The dialog body (DialogContent) scrolls internally; DialogTitle and
  // DialogActions don't. A short viewport (~460px, e.g. a laptop with the
  // browser chrome/toolbars eating vertical space) can leave DialogContent's
  // scrollHeight well past its clientHeight, so anything placed at the
  // bottom of the content — the save-error alert, the consequence panel —
  // can render fully below the fold with no scroll indicator. These two
  // refs let both cases pull themselves into view instead of assuming a
  // tall desktop viewport.
  const saveErrorRef = useRef(null);
  const consequenceRef = useRef(null);

  useEffect(() => {
    if (open && school) {
      const current = tzOptions.find((o) => o.id === school.timezone) || null;
      setSelected(current);
      setSaveError('');
      setSaving(false);
    }
  }, [open, school, tzOptions]);

  const hasChange = !!selected && school && selected.id !== school.timezone;
  const currentClock = useLiveClock(school?.timezone);
  const targetClock = useLiveClock(hasChange ? selected.id : null);

  // Consequence panel is progressive disclosure inside the scrollable
  // content area — when it first appears, bring it into view rather than
  // leaving it below the fold at short viewport heights.
  useEffect(() => {
    if (hasChange) {
      consequenceRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [hasChange]);

  // Save errors are moved out of the scrollable content and into
  // DialogActions (always-visible dialog chrome, never clipped by internal
  // scroll — see handleSave/render below), but a failure should still grab
  // attention and be announced to assistive tech, not just render silently
  // at the bottom. Moving focus does both: it triggers native browser
  // scroll-into-view for keyboard/AT users, and role="alert" + aria-live
  // covers screen readers even without a focus change.
  useEffect(() => {
    if (saveError) {
      saveErrorRef.current?.focus();
    }
  }, [saveError]);

  const handleSave = async () => {
    if (!hasChange || !school) return;
    setSaving(true);
    setSaveError('');
    // A dropped connection shouldn't strand the admin on a disabled dialog
    // indefinitely — give the request a ceiling and surface it as a normal
    // (retryable) save error.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/admin/schools/timezone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ school: school.name, timezone: selected.id }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Could not update timezone (HTTP ${res.status}).`);
      }
      // Deliberately does NOT include the count fields (student_count,
      // class_count, time_slot_count) — onSaved merges this into the existing
      // row rather than replacing it, so those counts are never zeroed out.
      const updated = await res.json();
      setSaving(false);
      onSaved(updated);
    } catch (err) {
      setSaving(false);
      setSaveError(
        err.name === 'AbortError'
          ? 'The request took too long and timed out. Please try again.'
          : err.message || 'Could not update timezone. Please try again.'
      );
    } finally {
      clearTimeout(timeoutId);
    }
  };

  if (!school) return null;

  const slotCount = school.time_slot_count;
  const hasSlotCount = typeof slotCount === 'number';

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, pb: 0.5 }}>
        Change timezone — {school.name}
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Group sessions and student sign-in windows for {school.name}
          {hasSlotCount
            ? ` — including its ${slotCount.toLocaleString()} configured time slot${slotCount === 1 ? '' : 's'} —`
            : ''}{' '}
          are evaluated in this school&rsquo;s timezone. The change applies immediately after
          saving.
        </Typography>

        <TimezoneAutocomplete
          value={selected}
          onChange={setSelected}
          tzOptions={tzOptions}
          tzLoadError={tzLoadError}
          disabled={saving}
          autoFocus
        />

        {/* ── Consequence summary — only shown once a real change is staged ── */}
        {hasChange && (
          <Box
            ref={consequenceRef}
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: adminColors.accentLight,
              border: `1px solid ${adminColors.accentBorder}`,
            }}
          >
            <Box display="flex" alignItems="center" gap={0.75} mb={1.25}>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: adminColors.accentDark }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#3730a3' }}>
                What this changes
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.5} sx={{ mb: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: adminColors.accentDark, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  Now
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>{formatTzName(school.timezone)}</Typography>
                <Typography variant="caption" color="text.secondary">{currentClock || '—'}</Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: '#818cf8', flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: adminColors.accentDark, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  After saving
                </Typography>
                <Typography variant="body2" fontWeight={600} noWrap>{formatTzName(selected.id)}</Typography>
                <Typography variant="caption" color="text.secondary">{targetClock || '—'}</Typography>
              </Box>
            </Box>

            <Typography variant="caption" sx={{ color: '#3730a3', display: 'block', lineHeight: 1.5 }}>
              Any weekly time slot (e.g. &ldquo;Tuesdays 09:00–10:00&rdquo;) will now open and close at
              that clock time in {formatTzName(selected.id)} instead of {formatTzName(school.timezone)} —
              double-check this is the change you intend.
            </Typography>
          </Box>
        )}

      </DialogContent>
      {/*
        The save-error alert lives here, in DialogActions, rather than at the
        bottom of DialogContent. DialogContent is the only part of this
        dialog that scrolls internally — at a short viewport (~460px) its
        scrollHeight can run well past clientHeight, and an alert appended
        after the consequence panel ends up entirely below the fold with no
        scroll affordance drawing the eye to it. On the one action where a
        silent-looking failure is most dangerous, that's not acceptable.
        DialogActions is always-visible dialog chrome (never clipped by the
        content scroll) and sits directly above the Save button that caused
        the error, so it can't be scrolled away from. role="alert" +
        aria-live="assertive" plus a focus move on appearance (see the
        useEffect above) also covers keyboard and screen-reader users, who
        previously got no signal at all on failure.
      */}
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: saveError ? 1.5 : 0 }}>
        {saveError && (
          <Alert
            ref={saveErrorRef}
            severity="error"
            role="alert"
            aria-live="assertive"
            tabIndex={-1}
            sx={{ width: '100%' }}
          >
            {saveError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChange || saving || !!tzLoadError}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: adminColors.accent,
              '&:hover': { backgroundColor: adminColors.accentDark },
            }}
          >
            {saving ? 'Saving…' : 'Save timezone'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Table (catalog case) ──────────────────────────────────────────────────
// Rendered for every MASTER_ADMIN/legacy-admin view of the schools catalog,
// regardless of row count (0/1/many) — a SCHOOL_ADMIN/TEACHER's permanently
// one-row view stays on <SchoolDetailCard> below instead, since create/
// rename/merge only make sense against a real, sortable/filterable catalog.
// Search + sort are wired through useQueryState (see SCHOOLS_QUERY_DEFAULTS)
// so a filtered/sorted view is a shareable URL, same as Students/Classes/
// Invites. Built on the shared DataTable/FilterBar/EmptyState primitives
// rather than a bespoke Table, so it gets sticky headers, the horizontal-
// scroll shadow treatment, and skeleton/empty states for free.

const SCHOOLS_QUERY_DEFAULTS = { q: '', sort: 'name', order: 'asc' };

// One Menu instance per row (MUI's own recommended pattern) rather than a
// single shared Menu keyed off "which row is active" — simpler state, and
// there's never more than a handful of rows on this page.
function SchoolRowActionsMenu({ school, canMerge, onRename, onMerge }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const buttonId = `school-row-actions-${school.id}`;

  return (
    <>
      <Tooltip title={`More actions for ${school.name}`}>
        <IconButton
          id={buttonId}
          size="small"
          aria-label={`More actions for ${school.name}`}
          aria-haspopup="menu"
          aria-expanded={open || undefined}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            ...TOUCH_HIT_SX,
            color: adminColors.inkSoft,
            '&:hover': { color: adminColors.ink, backgroundColor: adminColors.surface },
            '&:focus-visible': { outline: `2px solid ${adminColors.accent}`, outlineOffset: 2 },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} MenuListProps={{ 'aria-labelledby': buttonId }}>
        <MenuItem onClick={() => { setAnchorEl(null); onRename(school); }}>
          <ListItemIcon><RenameIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Rename</ListItemText>
        </MenuItem>
        <Tooltip title={canMerge ? '' : 'Add another school first — there is nothing to merge this one into.'}>
          {/* span wrapper so the Tooltip still fires on a disabled MenuItem */}
          <span>
            <MenuItem onClick={() => { setAnchorEl(null); onMerge(school); }} disabled={!canMerge}>
              <ListItemIcon><CallMergeIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Merge into another school…</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </>
  );
}

function SchoolsTable({
  schools,
  queryState,
  setQueryState,
  canManage,
  onChangeClick,
  onScheduleClick,
  onAddClick,
  onRenameClick,
  onMergeClick,
  onRefresh,
  refreshing,
  lastUpdatedAt,
}) {
  const q = (queryState.q || '').trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!q) return schools;
    return schools.filter((s) => s.name.toLowerCase().includes(q));
  }, [schools, q]);

  const sorted = useMemo(() => {
    const { sort, order } = queryState;
    const rows = [...filtered];
    rows.sort((a, b) => {
      let av = a[sort];
      let bv = b[sort];
      if (typeof av === 'string' || typeof bv === 'string') {
        av = (av || '').toString().toLowerCase();
        bv = (bv || '').toString().toLowerCase();
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1;
      if (av > bv) return 1;
      return 0;
    });
    if (order === 'desc') rows.reverse();
    return rows;
  }, [filtered, queryState]);

  // A row can only be a merge SOURCE or TARGET via POST .../{id}/merge, which
  // takes real School ids on both ends — a school that only exists as a
  // Group.school string (school.id === null, synthesized by the backend's
  // list_schools; see that endpoint's docstring) has nothing to merge.
  const realSchoolCount = useMemo(() => schools.filter((s) => s.id != null).length, [schools]);

  const asOf = lastUpdatedAt
    ? lastUpdatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
    : null;

  const columns = useMemo(() => [
    {
      key: 'name',
      label: 'School',
      sortable: true,
      render: (school) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: adminColors.ink }}>{school.name}</Typography>
          {school.possible_duplicate_of && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
              <WarningAmberIcon sx={{ fontSize: 14, color: '#b45309', flexShrink: 0 }} />
              {/* #b45309 — the same AA-verified (5.02:1 on white) amber
                  constants/roles.js already uses for the Student role chip,
                  reused here rather than a new one-off warning hex. */}
              <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 600 }}>
                {school.id != null
                  ? `Possible duplicate of ${school.possible_duplicate_of.name}`
                  : `Matches ${school.possible_duplicate_of.name} — no record of its own yet`}
              </Typography>
              {canManage && school.id != null && (
                <Button
                  size="small"
                  onClick={() => onMergeClick(school)}
                  sx={{
                    ...TOUCH_HIT_SX,
                    minWidth: 0,
                    py: 0,
                    px: 0.75,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'none',
                    color: adminColors.accentDark,
                    '&:hover': { backgroundColor: adminColors.accentLight },
                  }}
                >
                  Merge now
                </Button>
              )}
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'student_count',
      label: 'Students',
      align: 'right',
      sortable: true,
      render: (school) => <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(school.student_count)}</Box>,
    },
    {
      key: 'class_count',
      label: 'Classes',
      align: 'right',
      sortable: true,
      render: (school) => <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(school.class_count)}</Box>,
    },
    {
      key: 'time_slot_count',
      label: (
        <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
          Time slots
          <Tooltip title="Weekly time slots that open and close according to this school's timezone.">
            {/* color="action" measures ~4.5:1 on this table head's white
                background — matches the equivalent "Access" info icon in
                UsersView (AdminDashboardV2.js) rather than a one-off gray. */}
            <InfoOutlinedIcon sx={{ fontSize: 16 }} color="action" />
          </Tooltip>
        </Box>
      ),
      align: 'right',
      sortable: true,
      render: (school) => <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(school.time_slot_count)}</Box>,
    },
    {
      key: 'timezone',
      label: 'Timezone',
      sortable: true,
      render: (school) => {
        // Evaluated IN the school's own zone, not the admin's browser zone —
        // this is an absolute instant from the API, and the whole point of
        // this line is showing what time it is *there*.
        const localTime = school.current_local_time
          ? new Date(school.current_local_time).toLocaleString('en-US', {
              timeZone: school.timezone,
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            })
          : '—';
        const abbr = getTzAbbreviation(school.timezone);
        return (
          <Box>
            <Box display="flex" alignItems="center" gap={0.75}>
              <Typography variant="body2" fontWeight={600} sx={{ color: adminColors.ink }}>
                {formatTzName(school.timezone)}
              </Typography>
              {abbr && (
                <Chip
                  label={abbr}
                  size="small"
                  sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: adminColors.accentLight, color: adminColors.accentDark }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', display: 'block', mt: 0.25 }}>
              {localTime}
            </Typography>
          </Box>
        );
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (school) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onChangeClick(school)}
            sx={{
              ...TOUCH_HIT_SX,
              borderColor: adminColors.accentDark,
              color: adminColors.accentDark,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: adminColors.accentDark, backgroundColor: adminColors.accentLight },
            }}
          >
            Change timezone
          </Button>
          <Tooltip title={school.id == null ? 'Add this school first — there is no record to attach a schedule to yet.' : ''}>
            <span>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onScheduleClick(school)}
                disabled={school.id == null}
                sx={{
                  ...TOUCH_HIT_SX,
                  borderColor: adminColors.accentDark,
                  color: adminColors.accentDark,
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: adminColors.accentDark, backgroundColor: adminColors.accentLight },
                }}
              >
                Schedule
              </Button>
            </span>
          </Tooltip>
          {canManage && school.id != null && (
            <SchoolRowActionsMenu
              school={school}
              canMerge={realSchoolCount > 1}
              onRename={onRenameClick}
              onMerge={onMergeClick}
            />
          )}
        </Box>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- realSchoolCount/canManage/on* handlers are stable enough per render; recomputing columns is cheap and keeps this list readable as a flat literal
  ], [canManage, realSchoolCount, onChangeClick, onScheduleClick, onMergeClick, onRenameClick]);

  const emptyState = schools.length === 0 ? (
    <EmptyState
      icon={<DomainIcon />}
      title="No schools yet"
      description="Add your first school to start managing its timezone, classes, and enrollment."
      action={canManage ? (
        <Button
          variant="contained"
          onClick={onAddClick}
          startIcon={<AddIcon />}
          sx={{ backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
        >
          Add school
        </Button>
      ) : null}
    />
  ) : (
    <EmptyState
      icon={<SearchIcon />}
      title="No schools match your search"
      description={`Nothing matches “${queryState.q}”. Try a different name or clear the search.`}
      action={
        <Button size="small" onClick={() => setQueryState({ q: '' })} sx={{ fontWeight: 700, color: adminColors.accent }}>
          Clear search
        </Button>
      }
    />
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <FilterBar
          queryState={queryState}
          setQueryState={setQueryState}
          defaults={SCHOOLS_QUERY_DEFAULTS}
          searchKey="q"
          searchPlaceholder="Search schools by name"
          resetOnSearch={[]}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Staleness cue — the local-time column is a snapshot, only ever
              as fresh as the last manual refresh. */}
          {asOf && (
            <Typography variant="caption" color="text.secondary">
              {refreshing ? 'Refreshing…' : `As of ${asOf}`}
            </Typography>
          )}
          <Tooltip title="Refresh current local times">
            <span>
              <IconButton onClick={onRefresh} size="small" aria-label="Refresh current local times" disabled={refreshing} sx={TOUCH_HIT_SX}>
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
        <DataTable
          columns={columns}
          rows={sorted}
          getRowId={(row) => row.id ?? `name:${row.name}`}
          sortKey={queryState.sort}
          sortDirection={queryState.order}
          onSortChange={(key, direction) => setQueryState({ sort: key, order: direction })}
          emptyState={emptyState}
        />
      </Box>
    </Box>
  );
}

// ─── Add school dialog ──────────────────────────────────────────────────────

function AddSchoolDialog({ open, tzOptions, tzLoadError, onClose, onCreated }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName('');
      setTimezone(null);
      setError('');
      setSaving(false);
    }
  }, [open]);

  // Same "move focus to the error, don't just render it silently" pattern
  // as TimezoneChangeDialog's saveError — see that component's comment for
  // why (role="alert"/aria-live alone doesn't cover every AT, and moving
  // focus also scrolls it into view for keyboard users).
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const trimmedName = name.trim();
  const canSave = trimmedName.length > 0 && !!timezone && !saving;

  const handleCreate = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/schools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: trimmedName, timezone: timezone.id }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      onCreated(data);
    } catch (err) {
      // Surfaced inline (here), not as a toast — a 409 collision needs to
      // stay attached to the exact field it's complaining about, with the
      // dialog still open and the admin's typed name still in the field.
      setError(describeAdminError(err, "This school couldn't be created."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Add a school</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
          <TextField
            label="School name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            required
            disabled={saving}
            sx={{ '& .MuiOutlinedInput-root': TOUCH_HIT_SX }}
          />
          <TimezoneAutocomplete
            value={timezone}
            onChange={setTimezone}
            tzOptions={tzOptions}
            tzLoadError={tzLoadError}
            disabled={saving}
            label="Timezone"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: error ? 1.5 : 0 }}>
        {error && (
          <Alert ref={errorRef} severity="error" role="alert" aria-live="assertive" tabIndex={-1} sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
          >
            {saving ? 'Adding…' : 'Add school'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Rename school dialog ───────────────────────────────────────────────────
// Renaming rewrites the school's name across all nine denormalized `.school`
// columns (app/school_columns.py) in one backend transaction — the notice
// below is always visible, not progressive disclosure gated on "did the
// name actually change", so it's read before the admin commits to typing a
// new one, not after.

function RenameSchoolDialog({ open, school, tzOptions, tzLoadError, onClose, onRenamed }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);

  useEffect(() => {
    if (open && school) {
      setName(school.name);
      setTimezone(tzOptions.find((o) => o.id === school.timezone) || null);
      setError('');
      setSaving(false);
    }
  }, [open, school, tzOptions]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  if (!school) return null;

  const trimmedName = name.trim();
  const nameChanged = trimmedName.length > 0 && trimmedName !== school.name;
  const timezoneChanged = !!timezone && timezone.id !== school.timezone;
  const canSave = trimmedName.length > 0 && (nameChanged || timezoneChanged) && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const payload = {};
      if (nameChanged) payload.name = trimmedName;
      if (timezoneChanged) payload.timezone = timezone.id;
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/schools/${school.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      onRenamed(data, { nameChanged, oldName: school.name });
    } catch (err) {
      setError(describeAdminError(err, "This school couldn't be updated."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Rename {school.name}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="small" sx={{ color: adminColors.accentDark }} />}
          sx={{ mb: 2.5, backgroundColor: adminColors.accentLight, color: '#3730a3' }}
        >
          This name is stored in nine places — every student, class, time slot, curriculum item, tutoring
          session, invitation, and safety record tied to {school.name} updates to match, in one step, the
          moment you save.
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="School name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            fullWidth
            required
            disabled={saving}
            sx={{ '& .MuiOutlinedInput-root': TOUCH_HIT_SX }}
          />
          <TimezoneAutocomplete
            value={timezone}
            onChange={setTimezone}
            tzOptions={tzOptions}
            tzLoadError={tzLoadError}
            disabled={saving}
            label="Timezone"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: error ? 1.5 : 0 }}>
        {error && (
          <Alert ref={errorRef} severity="error" role="alert" aria-live="assertive" tabIndex={-1} sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Merge schools dialog ────────────────────────────────────────────────────
// The deck's duplicate-merge flow: destructive and irreversible (the source
// school row is deleted server-side the moment this succeeds — see POST
// /admin/schools/{id}/merge), so every step here is designed to make that
// unmistakable rather than a single "are you sure?" bolt-on. The target is
// never auto-selected, even when a `possible_duplicate_of` hint is
// available — "Use suggested match" only *fills* the field, it's still the
// admin's own explicit choice to confirm from there.

function MergeSchoolsDialog({ open, source, candidates, onClose, onMergeSucceeded }) {
  const navigate = useNavigate();
  const [targetId, setTargetId] = useState('');
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const errorRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTargetId('');
      setMerging(false);
      setError('');
      setResult(null);
    }
  }, [open]);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  if (!source) return null;

  const suggested =
    source.possible_duplicate_of && candidates.some((c) => c.id === source.possible_duplicate_of.id)
      ? source.possible_duplicate_of
      : null;
  const target = candidates.find((c) => c.id === targetId) || null;

  const handleMerge = async () => {
    if (!target || merging) return;
    setMerging(true);
    setError('');
    try {
      const res = await authFetch(`${config.API_BASE_URL}/api/admin/schools/${source.id}/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ into_school_id: target.id }),
      });
      const data = await handleApiResponse(res, navigate);
      if (data === null) return; // 401 — already redirected
      setResult(data);
      // Fired the moment the merge actually lands — the underlying catalog
      // (counts, the deleted source row, duplicate flags) is already stale
      // by the time this resolves, so the table refetches in the
      // background while this dialog keeps showing the result. onClose
      // (Done/backdrop/Esc) is a separate step — see below.
      onMergeSucceeded();
    } catch (err) {
      setError(describeAdminError(err, "This merge couldn't be completed."));
    } finally {
      setMerging(false);
    }
  };

  const movedEntries = result ? Object.entries(result.moved_row_counts || {}) : [];
  const movedTotal = movedEntries.reduce((sum, [, n]) => sum + n, 0);

  return (
    <Dialog open={open} onClose={merging ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>{result ? 'Merge complete' : `Merge ${source.name}`}</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {result ? (
          <Box>
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{result.source_school_name}</strong> has been deleted. Everything it had moved to{' '}
              <strong>{result.target_school_name}</strong>.
            </Typography>
            {movedTotal === 0 ? (
              // Honest zero, not a suppressed/empty-looking panel — a merge
              // that moved nothing is still a real, reportable outcome.
              <Alert severity="info">
                Nothing moved — {result.source_school_name} had no students, classes, or time slots.
              </Alert>
            ) : (
              <Box sx={{ borderRadius: radius.control, border: `1px solid ${adminColors.accentBorder}`, backgroundColor: adminColors.accentLight, p: 2 }}>
                {movedEntries.map(([table, count]) => (
                  <Box key={table} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#3730a3', textTransform: 'capitalize' }}>
                      {table.replace(/_/g, ' ')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: adminColors.accentDark, fontVariantNumeric: 'tabular-nums' }}>
                      {count.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Choose the school that should survive. Every student, class, and time slot at{' '}
              <strong>{source.name}</strong> moves there, and {source.name} is then deleted.
            </Typography>

            {suggested && targetId !== suggested.id && (
              <Button
                size="small"
                onClick={() => setTargetId(suggested.id)}
                sx={{
                  mb: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  color: adminColors.accentDark,
                  backgroundColor: adminColors.accentLight,
                  '&:hover': { backgroundColor: adminColors.accentBorder },
                }}
              >
                Use suggested match: {suggested.name}
              </Button>
            )}

            <FormControl fullWidth required sx={{ mb: 2.5 }}>
              <InputLabel id="merge-target-label">Surviving school</InputLabel>
              <Select
                labelId="merge-target-label"
                label="Surviving school"
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                disabled={merging}
                sx={{ '& .MuiSelect-select': TOUCH_HIT_SX }}
              >
                {candidates.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name} · {formatCount(c.student_count)} students · {formatCount(c.class_count)} classes
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {target && (
              <Alert severity="error" icon={<WarningAmberIcon fontSize="small" />} sx={{ alignItems: 'flex-start' }}>
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>This can&rsquo;t be undone.</Typography>
                <Typography variant="body2">
                  {formatCount(source.student_count)} student{source.student_count === 1 ? '' : 's'},{' '}
                  {formatCount(source.class_count)} class{source.class_count === 1 ? '' : 'es'}, and{' '}
                  {formatCount(source.time_slot_count)} time slot{source.time_slot_count === 1 ? '' : 's'} move
                  from <strong>{source.name}</strong> to <strong>{target.name}</strong>. {source.name} is then
                  permanently deleted.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, flexDirection: 'column', alignItems: 'stretch', gap: error ? 1.5 : 0 }}>
        {error && (
          <Alert ref={errorRef} severity="error" role="alert" aria-live="assertive" tabIndex={-1} sx={{ width: '100%' }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {result ? (
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
            >
              Done
            </Button>
          ) : (
            <>
              <Button onClick={onClose} disabled={merging}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleMerge}
                disabled={!target || merging}
                startIcon={merging ? <CircularProgress size={16} color="inherit" /> : null}
                sx={TOUCH_HIT_SX}
              >
                {merging ? 'Merging…' : target ? `Merge into ${target.name}` : 'Merge'}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── Single-school detail (structural ≤1-row case) ───────────────────────────
// A SCHOOL_ADMIN's school list isn't a filtered-down result — it's *always*
// exactly one row, permanently, by construction of the role. That's a
// different situation from an All Users/Classes & Schedules search that happens to
// return one row today, so it doesn't get the same table treatment. Reuses
// the Dashboard Overview stat-card idiom (Card/CardContent, caption label +
// large colored value, borderRadius 2 — see AdminDashboardV2's stat cards)
// rather than a new bespoke hero, so it reads as part of the existing system.
// The table above stays reserved for 2+ schools, where rows are genuinely
// tabular and worth filtering.

function SchoolDetailCard({ school, onChangeClick, onScheduleClick, onRefresh, refreshing, lastUpdatedAt }) {
  const abbr = getTzAbbreviation(school.timezone);
  const localTime = school.current_local_time
    ? new Date(school.current_local_time).toLocaleString('en-US', {
        timeZone: school.timezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '—';
  const asOf = lastUpdatedAt
    ? lastUpdatedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })
    : null;

  // Colors reused verbatim from the Dashboard Overview stat cards
  // (AdminDashboardV2.js) rather than invented for this component.
  const stats = [
    { label: 'Students', value: formatCount(school.student_count), icon: <PeopleIcon sx={{ fontSize: 26 }} />, color: adminColors.accent, bg: adminColors.accentLight },
    { label: 'Classes', value: formatCount(school.class_count), icon: <ClassIcon sx={{ fontSize: 26 }} />, color: '#388e3c', bg: '#e8f5e9' },
    { label: 'Time Slots', value: formatCount(school.time_slot_count), icon: <ScheduleIcon sx={{ fontSize: 26 }} />, color: '#7b1fa2', bg: '#f3e5f5' },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 1,
          mb: 2.5,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#18181b' }}>
          {school.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {asOf && (
            <Typography variant="caption" color="text.secondary">
              {refreshing ? 'Refreshing…' : `As of ${asOf}`}
            </Typography>
          )}
          <Tooltip title="Refresh current local time">
            <span>
              <IconButton onClick={onRefresh} size="small" aria-label="Refresh current local time" disabled={refreshing}>
                {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2.5, opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
        {stats.map(({ label, value, icon, color, bg }) => (
          <Grid size={{ xs: 12, sm: 4 }} key={label}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}
                    >
                      {label}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color, lineHeight: 1.2, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                      {value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color,
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper
        sx={{
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          backgroundColor: 'white',
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11, fontWeight: 500 }}
          >
            Timezone
          </Typography>
          <Box display="flex" alignItems="center" gap={0.75} sx={{ mt: 0.5 }}>
            <Typography variant="body1" fontWeight={600} sx={{ color: '#18181b' }}>
              {formatTzName(school.timezone)}
            </Typography>
            {abbr && (
              <Chip
                label={abbr}
                size="small"
                sx={{ height: 20, fontSize: 11, fontWeight: 700, backgroundColor: adminColors.accentLight, color: adminColors.accentDark }}
              />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums', display: 'block', mt: 0.25 }}>
            {localTime}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => onScheduleClick(school)}
            sx={{
              borderColor: adminColors.accentDark,
              color: adminColors.accentDark,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: adminColors.accentDark, backgroundColor: adminColors.accentLight },
            }}
          >
            Schedule
          </Button>
          <Button
            variant="outlined"
            onClick={() => onChangeClick(school)}
            sx={{
              borderColor: adminColors.accentDark,
              color: adminColors.accentDark,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: adminColors.accentDark, backgroundColor: adminColors.accentLight },
            }}
          >
            Change timezone
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

// ─── Main view ────────────────────────────────────────────────────────────────

const SchoolManagement = ({ currentUser }) => {
  const [schools, setSchools] = useState([]);
  // `loading` gates the full-page spinner and is only ever true for the very
  // first load. Subsequent calls (manual Refresh, retry-after-error) use
  // `refreshing` instead, so the already-rendered table stays mounted — no
  // dropped filter text, no full-page spinner for a "refresh current time" click.
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [tzLists, setTzLists] = useState({ us: [], all: [] });
  const [tzLoadError, setTzLoadError] = useState('');

  const [dialogSchool, setDialogSchool] = useState(null);
  const [scheduleSchool, setScheduleSchool] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Create/rename/merge are MASTER_ADMIN / legacy "admin" only server-side
  // (`_require_super_admin` — see app/routes/admin.py) — a SCHOOL_ADMIN
  // never sees any of these three dialogs' trigger affordances, not even
  // disabled ones.
  const canManageCatalog = isMasterAdminRole(currentUser?.role);
  const [queryState, setQueryState] = useQueryState(SCHOOLS_QUERY_DEFAULTS);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [renameSchool, setRenameSchool] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);

  const hasLoadedRef = useRef(false);
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);
  // Guards every setState reachable from an async resolution below. Neither
  // fetch here is cancelled on unmount (requestIdRef only protects against a
  // *stale* response beating a fresher one, not against an admin navigating
  // away mid-flight — e.g. clicking another sidebar item right after opening
  // this page), so both `.then`/`.catch` chains check this before touching
  // state.
  const isMountedRef = useRef(true);

  const loadSchools = useCallback(() => {
    // Cancel any in-flight request first — otherwise a slow response from an
    // earlier call (e.g. double-clicking Refresh, or a slow mount racing a
    // manual refresh) can resolve after a newer one and silently overwrite
    // fresher state.
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    if (hasLoadedRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');

    return fetch(`${config.API_BASE_URL}/api/admin/schools`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          if (res.status === 401) throw new Error('Your session has expired. Please sign in again.');
          if (res.status === 403) throw new Error('You do not have permission to view schools.');
          // A SCHOOL_ADMIN with no school assigned gets a 400 here (not an
          // empty list — see app/routes/admin.py's `list_schools`) with a
          // human-readable `detail`; surface it instead of a bare status code.
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Could not load schools (HTTP ${res.status}).`);
        }
        return res.json();
      })
      .then((data) => {
        if (requestId !== requestIdRef.current || !isMountedRef.current) return;
        setSchools(data.schools || []);
        setLastUpdatedAt(new Date());
        hasLoadedRef.current = true;
      })
      .catch((err) => {
        if (err.name === 'AbortError' || requestId !== requestIdRef.current || !isMountedRef.current) return;
        setError(err.message || 'Could not load schools.');
        hasLoadedRef.current = true;
      })
      .finally(() => {
        if (requestId !== requestIdRef.current || !isMountedRef.current) return;
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    loadSchools();
    fetch(`${config.API_BASE_URL}/api/admin/schools/timezones`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load the timezone list (HTTP ${res.status}).`);
        return res.json();
      })
      .then((data) => {
        if (!isMountedRef.current) return;
        setTzLists({ us: data.us || [], all: data.all || [] });
      })
      .catch((err) => {
        if (!isMountedRef.current) return;
        setTzLoadError(err.message || 'Could not load the timezone list.');
      });
    return () => {
      isMountedRef.current = false;
      // Also cancel the in-flight /api/admin/schools request itself (the
      // /timezones fetch above has no AbortController — it's a single fire-
      // and-forget call with no cancellation-race to protect against, so the
      // isMountedRef check alone is sufficient there).
      abortRef.current?.abort();
    };
  }, [loadSchools]);

  // Build a flat, grouped option list once — "United States" surfaced first,
  // the remaining ~400 zones grouped underneath. Computed once per fetch, not
  // per keystroke.
  const tzOptions = useMemo(() => {
    const usSet = new Set(tzLists.us);
    const us = tzLists.us.map((id) => ({
      id,
      group: 'United States',
      label: formatTzName(id),
      abbr: getTzAbbreviation(id),
    }));
    const rest = tzLists.all
      .filter((id) => !usSet.has(id))
      .map((id) => ({
        id,
        group: 'All time zones',
        label: formatTzName(id),
        abbr: getTzAbbreviation(id),
      }));
    return [...us, ...rest];
  }, [tzLists]);

  const handleSaved = (updated) => {
    // Spread order matters: `updated` only ever contains {name, timezone,
    // current_local_time} (see PUT /api/admin/schools/timezone), so this
    // merges the new timezone/local-time in without touching the existing
    // row's student_count/class_count/time_slot_count.
    setSchools((prev) => prev.map((s) => (s.name === updated.name ? { ...s, ...updated } : s)));
    setDialogSchool(null);
    setSnackbar({ open: true, message: `${updated.name}'s timezone is now ${formatTzName(updated.timezone)}.` });
  };

  // Create/rename/merge all refetch the full catalog rather than hand-
  // patching `schools` in place: a create adds a row with counts the POST
  // response doesn't carry, a rename can shuffle `possible_duplicate_of`
  // hints across other rows, and a merge deletes a row and changes the
  // target's counts — a fresh GET is the only source of truth honest about
  // all three at once. `loadSchools` already knows to use the lighter
  // `refreshing` (dimmed, not a full-page spinner) path once the first load
  // has landed.
  const handleSchoolCreated = (created) => {
    setAddDialogOpen(false);
    loadSchools();
    setSnackbar({ open: true, message: `"${created.name}" was added.` });
  };

  const handleRenamed = (data, { nameChanged, oldName }) => {
    setRenameSchool(null);
    loadSchools();
    if (!nameChanged) {
      setSnackbar({ open: true, message: `${data.name}'s timezone is now ${formatTzName(data.timezone)}.` });
      return;
    }
    const counts = data.renamed_row_counts || {};
    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
    const tableCount = Object.keys(counts).length;
    setSnackbar({
      open: true,
      message: total > 0
        ? `Renamed "${oldName}" to "${data.name}" — updated ${total.toLocaleString()} record${total === 1 ? '' : 's'} across ${tableCount} table${tableCount === 1 ? '' : 's'}.`
        : `Renamed "${oldName}" to "${data.name}". No other records referenced the old name.`,
    });
  };

  // Once the first load has succeeded, a later failed refresh shouldn't tear
  // down the table that's already on screen — it becomes a non-blocking
  // banner above the (still-valid, just possibly stale) last-known data.
  const hasData = lastUpdatedAt !== null;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f7f7f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: loading ? 3 : 1 }}>
        {/* Matches the sidebar nav label exactly (AdminDashboardV2.js's
            `schoolManagement` menu item uses this same isMasterAdminRole
            check) — a SCHOOL_ADMIN's single row here is their own school,
            not a catalog, so the heading has to stop calling itself
            "Schools" (plural) once the nav item stops promising one. */}
        <Typography variant="h4" sx={{ color: '#18181b', fontWeight: 600 }}>
          {canManageCatalog ? 'Schools' : 'My School'}
        </Typography>
        {/* Add school is a page-level action, not a row action — it's
            offered even while the catalog is empty (see SchoolsTable's own
            "No schools yet" empty state, which repeats this same action). */}
        {canManageCatalog && !loading && (
          <Button
            variant="contained"
            onClick={() => setAddDialogOpen(true)}
            startIcon={<AddIcon />}
            sx={{ ...TOUCH_HIT_SX, backgroundColor: adminColors.accent, '&:hover': { backgroundColor: adminColors.accentDark } }}
          >
            Add school
          </Button>
        )}
      </Box>

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={loadSchools}>
              Retry
            </Button>
          }
          sx={{ maxWidth: 560, mb: hasData ? 2 : 0, mt: hasData ? 0 : 1 }}
        >
          {error}
        </Alert>
      )}

      {/* Unreachable for a MASTER_ADMIN today (see SchoolsTable's own richer
          "No schools yet" empty state, with an Add school CTA, for that
          case) — GET /api/admin/schools always returns exactly one entry
          for a school-scoped caller with a school assigned (see
          app/routes/admin.py's list_schools), so this is specifically the
          "no school assigned" 400 path being defensive about an empty
          array too, not a state either role actually reaches. */}
      {!loading && !error && hasData && !canManageCatalog && schools.length === 0 && (
        <Alert severity="info" sx={{ maxWidth: 560, mt: 1 }}>
          No school is associated with your account yet. Contact a master admin to have one assigned.
        </Alert>
      )}

      {/* MASTER_ADMIN (and legacy "admin") manage the whole catalog —
          create/rename/merge only make sense there, so they always get the
          table, even with 0 or 1 rows (see SchoolsTable's own empty
          state for the 0-row case). Every other role's school list is a
          structural, permanent single row (their own school, every time —
          see SchoolDetailCard above), not an incidental filtered-down
          result, so it keeps the simpler card layout with timezone-only
          controls. */}
      {hasData && !canManageCatalog && schools[0] && (
        <SchoolDetailCard
          school={schools[0]}
          onChangeClick={(school) => setDialogSchool(school)}
          onScheduleClick={(school) => setScheduleSchool(school)}
          onRefresh={loadSchools}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
        />
      )}

      {hasData && canManageCatalog && (
        <SchoolsTable
          schools={schools}
          queryState={queryState}
          setQueryState={setQueryState}
          canManage={canManageCatalog}
          onChangeClick={(school) => setDialogSchool(school)}
          onScheduleClick={(school) => setScheduleSchool(school)}
          onAddClick={() => setAddDialogOpen(true)}
          onRenameClick={(school) => setRenameSchool(school)}
          onMergeClick={(school) => setMergeSource(school)}
          onRefresh={loadSchools}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
        />
      )}

      <TimezoneChangeDialog
        open={!!dialogSchool}
        school={dialogSchool}
        tzOptions={tzOptions}
        tzLoadError={tzLoadError}
        onClose={() => setDialogSchool(null)}
        onSaved={handleSaved}
      />

      <ScheduleTemplateDialog
        open={!!scheduleSchool}
        school={scheduleSchool}
        onClose={() => setScheduleSchool(null)}
        onSaved={(data) => {
          setScheduleSchool(null);
          setSnackbar({ open: true, message: `${data.school_name}'s schedule was saved.` });
        }}
      />

      {canManageCatalog && (
        <>
          <AddSchoolDialog
            open={addDialogOpen}
            tzOptions={tzOptions}
            tzLoadError={tzLoadError}
            onClose={() => setAddDialogOpen(false)}
            onCreated={handleSchoolCreated}
          />

          <RenameSchoolDialog
            open={!!renameSchool}
            school={renameSchool}
            tzOptions={tzOptions}
            tzLoadError={tzLoadError}
            onClose={() => setRenameSchool(null)}
            onRenamed={handleRenamed}
          />

          <MergeSchoolsDialog
            open={!!mergeSource}
            source={mergeSource}
            candidates={mergeSource ? schools.filter((s) => s.id != null && s.id !== mergeSource.id) : []}
            onClose={() => setMergeSource(null)}
            onMergeSucceeded={loadSchools}
          />
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert severity="success" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolManagement;
